/**
 * Extra detail for a report. Values are codes, IDs and route templates, never
 * personal or tenant data (D-003).
 */
export interface ErrorContext {
  /** Searchable labels, such as the request ID or the route template. */
  readonly tags?: Readonly<Record<string, string>>;
  /** React's component stack, for errors thrown while rendering. */
  readonly componentStack?: string;
  /** False when nothing caught the error, so the app may be left broken. Defaults to true. */
  readonly handled?: boolean;
}

/**
 * Sends unexpected errors to error tracking. Application code depends on this;
 * only the Sentry adapters in each app import the SDK.
 */
export interface ErrorReporter {
  captureException(error: unknown, context?: ErrorContext): void;
  /** Waits for queued reports to be sent; resolves `false` if the timeout passes first. */
  flush(timeoutMs: number): Promise<boolean>;
}

/** Used when no DSN is configured, and in tests. */
export const noopErrorReporter: ErrorReporter = {
  captureException: () => undefined,
  flush: () => Promise.resolve(true),
};

/** One report, as `fakeErrorReporter` records it. */
export interface ErrorReport {
  readonly error: unknown;
  readonly context: ErrorContext | undefined;
}

/** For tests: records every report instead of sending it. */
export function fakeErrorReporter(): ErrorReporter & { readonly reports: ErrorReport[] } {
  const reports: ErrorReport[] = [];
  return {
    reports,
    captureException: (error, context) => {
      reports.push({ error, context });
    },
    flush: () => Promise.resolve(true),
  };
}
